import RichTextEditor from "react-rte";
import { EditorValue } from "react-rte";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../app/store";
import CreateDiaryEntry from "../features/Diary/interfaces/CreateDiary";
import {
  createDiaryEntry,
  updateDiaryEntry,
  clearDiaryState,
  deleteDiaryEntry,
} from "../features/Diary/DiarySlice";
import NavbarScroller from "./Navbar";
import { privateNavigationLinks } from "../utils/navigationLinks";
import { useHistory, useLocation } from "react-router";
import Diary from "../features/Diary/interfaces/IDiary";
import { TextField, Modal, CircularProgress, SvgIcon } from "@mui/material";
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";
import toast from "react-hot-toast";
import DeleteDiaryEntry from "./DeleteDiaryEntry";
import CustomButton from "./CustomButton";
import "../styles/pages/diary-entry.scss";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import formatDateAndTime from "../utils/formatDateAndTime";
import getRandomPlaceholderQuote from "../utils/getPlaceHolderQuotes";

export interface ManagedRichTextEditorProps {
  placeholder?: string;
  onChange: (value: string) => void;
}

export interface Props {
  RichTextProps: ManagedRichTextEditorProps;
  default?: string;
}

const DiaryPage = ({ placeholder }: ManagedRichTextEditorProps) => {
  const location = useLocation();
  const { entry, entry_date, entry_id, entry_title } = location.state as Diary;
  const [isOldEntry, _] = useState(entry_id === -999 ? false : true);
  const [startDate, setStartDate] = useState(
    isOldEntry ? new Date(entry_date) : new Date()
  );

  const [isReadOnly, setIsReadOnly] = useState(
    entry_id === -999 ? false : true
  );

  const [editorState, setEditorState] = useState<EditorValue>(
    entry_id === -999
      ? RichTextEditor.createEmptyValue()
      : RichTextEditor.createValueFromString(entry, "markdown")
  );

  const [placeholderQuote, setPlaceholderQuote] = useState("");

  const handleChange = (event: EditorValue) => {
    setEditorState(event);
  };

  const dispatch = useDispatch();
  const history = useHistory();

  const {
    entries,
    diaryError,
    isDiaryError,
    isDiaryFetching,
    isDiarySuccess,
    diarySuccess,
  } = useSelector((state: RootState) => state.diary);

  const [diaryEntryData, setDiaryEntryData] = useState<CreateDiaryEntry>({
    entry: "",
    entry_date: "",
    entry_title: "",
    entry_id: -999,
  });

  const prepareForDispatch = async () => {
    const entryToSave = editorState.toString("markdown");

    //if the data is old one and not changed the title
    if (isOldEntry && diaryEntryData.entry_title === "") {
      setDiaryEntryData((prev) => ({
        ...prev,
        entry: entryToSave,
        entry_date: formatDateAndTime(startDate).formattedDate,
        entry_time: formatDateAndTime(startDate).formattedTime,
        entry_id,
        entry_title,
      }));
    }
    //title would be updated in the on change if it is changed
    else {
      setDiaryEntryData((prev) => ({
        ...prev,
        entry: entryToSave,
        entry_date: formatDateAndTime(startDate).formattedDate,
        entry_time: formatDateAndTime(startDate).formattedTime,
        entry_id,
      }));
    }
  };

  const createDiaryEntryDispatch = async () => {
    dispatch(createDiaryEntry(diaryEntryData));
  };

  const updateDiaryEntryDispatch = async () => {
    dispatch(updateDiaryEntry(diaryEntryData));
  };

  useEffect(() => {
    dispatch(clearDiaryState());
    setPlaceholderQuote(getRandomPlaceholderQuote());
  }, []);

  useEffect(() => {
    if (isDiaryError) {
      const errors = diaryError[0] as any;

      if (
        diaryError.every((value, i, diaryError) => value === diaryError[0]) &&
        diaryError.length > 1
      ) {
      }
      for (let i in diaryError) {
        if (diaryError[i] !== "Unauthorized") {
          toast.error(diaryError[i]);
        }
      }

      if (typeof errors !== "string") {
        for (let i in errors) {
          toast.error(errors[i]);
        }
      } else if (errors === "Unauthorized") {
      }
    }
  }, [isDiaryError, diaryError]);

  useEffect(() => {
    if (
      isDiarySuccess &&
      diarySuccess &&
      diarySuccess[0] === "Diary entry has been created!"
    ) {
      toast.success("Entry saved!");
    }
    if (
      isDiarySuccess &&
      diarySuccess &&
      diarySuccess[0] === "Diary entry has been updated!"
    ) {
      toast.success("Entry updated!");
    }

    if (
      isDiarySuccess &&
      diarySuccess &&
      diarySuccess[0] === "Diary entry deleted!"
    ) {
      toast.success("Entry deleted!");
      history.push("/diary");
    }
  }, [isDiarySuccess]);

  return (
    <div className="diary-entry">
      <NavbarScroller links={privateNavigationLinks.links} />

      <div className="go-back">
        <SvgIcon
          component={ArrowBackIcon}
          className="go-back--icon"
          onClick={() => {
            history.push("/diary");
          }}
        />
      </div>
      <TextField
        label="Entry title"
        required
        type="text"
        defaultValue={entry_id === -999 ? "" : entry_title}
        disabled={isReadOnly ? true : false}
        onChange={(e) => {
          setDiaryEntryData((prev) => ({
            ...prev,
            entry_title: e.target.value,
          }));
        }}
        className="diary-entry--title-field"
      />

      <RichTextEditor
        value={editorState}
        autoFocus={true}
        onChange={(value: any) => {
          handleChange(value);
        }}
        // placeholder={placeholder}
        placeholder={isOldEntry ? "" : placeholderQuote}
        readOnly={isReadOnly ? true : false}
        editorClassName="diary-entry--editor"
        editorStyle={
          isReadOnly
            ? { background: "rgba(177, 142, 107, 0.493)", cursor: "default" }
            : { background: "white" }
        }
      />
      <DatePicker
        selected={startDate}
        onChange={(date: Date) => setStartDate(date)}
        todayButton="Today"
        placeholderText="Click to select date"
        dateFormat="MMMM d, yyyy h:mm aa"
        disabled={isReadOnly ? true : false}
        className="diary-entry--calender"
        showTimeSelect
      />

      <div className="diary-entry--buttons">
        <div className="non-danger-buttons">
          <div className="save-buttons">
            <CustomButton
              displayText="Save"
              onClick={() => {
                isOldEntry
                  ? updateDiaryEntryDispatch()
                  : createDiaryEntryDispatch();
              }}
              className="save-button"
            />

            <CustomButton
              displayText="Save Draft"
              onClick={() => {
                prepareForDispatch();
              }}
              className="save-draft-button"
            />
          </div>

          <CustomButton
            displayText="Edit"
            onClick={() => {
              setIsReadOnly(!isReadOnly);
            }}
            style={{
              color: "black",
              border: "2px solid black",
              width: "100%",
              boxShadow: "5px 5px 0",
            }}
          />
        </div>
        <div className="delete-diary-entry">
          <DeleteDiaryEntry entry_id={entry_id} />
        </div>
      </div>
      {isDiaryFetching && (
        <div className="loader">
          <CircularProgress />
        </div>
      )}
    </div>
  );
};

export default DiaryPage;
